import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  fetchAdminPlatformFeatures,
  patchAdminPlatformFeature,
} from '@/api/(admin)/platform-features';
import type { AdminPlatformFeature } from '@/api/(admin)/platform-features/types';
import { useTranslator } from '@/hooks/useTranslator';

/**
 * Which platform features this company has, and the switch that changes it.
 *
 * The list is asked for with the company's id so each row carries that
 * company's `isEnabledForCompany` and its own `updatedAt` from the join —
 * without it the endpoint answers about the features alone.
 *
 * Only features active platform-wide are listed, as the old tab did: `isActive`
 * is the feature's own kill switch, and a company toggle on a dead feature
 * grants nothing.
 */
/** Shared by the tab's route loader and the hook, so the prefetch warms the
 *  key the component reads. */
export function companyPlatformFeaturesQueryOptions(companyId: string) {
  return queryOptions({
    queryKey: ['admin', 'platform-features', companyId] as const,
    queryFn: () =>
      fetchAdminPlatformFeatures(new URLSearchParams({ companyId }).toString()),
    enabled: Boolean(companyId),
    staleTime: 60_000,
  });
}

export function useCompanyPlatformFeatures(companyId: string) {
  const { getTranslation: t } = useTranslator({ page: 'managementCompanies' });
  const queryClient = useQueryClient();
  const queryKey = ['admin', 'platform-features', companyId] as const;

  const query = useQuery(companyPlatformFeaturesQueryOptions(companyId));

  const mutation = useMutation({
    mutationFn: ({
      feature,
      enabled,
    }: {
      feature: AdminPlatformFeature;
      enabled: boolean;
    }) =>
      patchAdminPlatformFeature(feature.id, {
        // The endpoint patches the feature itself, so its own fields ride along
        // untouched; the company lands in exactly one of the two lists.
        name: feature.name,
        ...(feature.description != null
          ? { description: feature.description }
          : {}),
        isActive: feature.isActive === 1 ? 1 : 0,
        ...(enabled
          ? { enableCompanies: [companyId] }
          : { disableCompanies: [companyId] }),
      }),
  });

  /**
   * Toggles awaiting their response, keyed by feature id.
   *
   * The switch reads from here first: the mutation invalidates the whole list,
   * and without this the switch would sit on its old value until the refetch
   * lands.
   */
  const [pending, setPending] = useState<Record<string, boolean>>({});

  async function setEnabled(
    feature: AdminPlatformFeature,
    enabled: boolean,
  ): Promise<void> {
    setPending(prev => ({ ...prev, [feature.id]: enabled }));
    try {
      await mutation.mutateAsync({ feature, enabled });
      toast.success(t('toast.saved'));
      await queryClient.invalidateQueries({ queryKey });
    } catch (error) {
      toast.error(t('errors.featureToggleFailed'), {
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      // Cleared either way: on success the refetched row carries the value, and
      // on failure the switch must snap back rather than lie.
      setPending(prev => {
        const next = { ...prev };
        delete next[feature.id];
        return next;
      });
    }
  }

  const features = (query.data?.data ?? []).filter(f => f.isActive === 1);

  return {
    features,
    isLoading: query.isLoading,
    isError: query.isError,
    setEnabled,
    /** What the switch should show — the in-flight value when there is one. */
    isEnabled: (feature: AdminPlatformFeature) =>
      pending[feature.id] ?? Boolean(feature.isEnabledForCompany),
    isSaving: (feature: AdminPlatformFeature) => feature.id in pending,
  };
}
